import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { View, ScrollView, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator, Image, Modal, Platform } from 'react-native';
import { supabase as supabaseClient } from '@cubemeet/core/services/supabase';
import PlatformIcon from '@cubemeet/ui/components/common/PlatformIcon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MdClose, MdLocationOn } from 'react-icons/md';
import NearbyMap from '../components/NearbyMap';
import { useTravelI18n } from '../modules/travel/useTravelI18n';

// 标签类型定义
interface TagFilter {
  type: 'tags' | 'category' | 'subcategory' | 'custom' | 'location';
  values: string[];
  label: string;
}

interface CardListPageProps {
  initialTagFilters?: TagFilter[];
  selectedTypes?: string[];
  selectedRegions?: string[];
  sortBy?: 'created_at' | 'popularity' | 'rating';
  viewMode?: 'grid' | 'list';
  title?: string;
  embedded?: boolean;
  /** 当提供时，列表项将显示“加入行程”按钮，点击后调用该回调，不会跳转详情 */
  onSelectCard?: (card: Card) => void;
  /** 是否在点击卡片时以 Modal 打开详情（轻量预览+操作），默认 false：直接跳转路由 */
  openDetailInModal?: boolean;
  /** 是否在卡片封面上显示“加入行程”悬浮按钮（仅当提供 onSelectCard 时生效），默认 false */
  showAddButton?: boolean;
}

interface Card {
  id: string;
  title: string;
  description?: string;
  address?: string;
  card_type: string;
  tags?: string[];
  category?: string;
  subcategory?: string;
  cover_image?: string;
  base_payload?: any;
  created_at: string;
  creator_id: string;
  visibility: string;
  status: string;
}

// Helper to extract coordinates
const extractCoords = (card: any): { lat: number; lng: number } | undefined => {
  if (!card) return undefined;
  
  const tryObj = (obj: any) => {
    if (!obj) return undefined;
    
    const parseNum = (v: any) => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && v.trim()) {
        const n = Number(v);
        return Number.isNaN(n) ? undefined : n;
      }
      return undefined;
    };

    // Handle array [lng, lat] or [lat, lng] directly
    if (Array.isArray(obj) && obj.length >= 2) {
       const n1 = parseNum(obj[0]);
       const n2 = parseNum(obj[1]);
       if (n1 !== undefined && n2 !== undefined) {
          // Heuristic: if abs(n1) > 90, it's likely lng.
          if (Math.abs(n1) > 90) return { lat: n2, lng: n1 };
          return { lat: n1, lng: n2 };
       }
    }

    // common shapes: { lat, lng } or { latitude, longitude } or { lat, lon }
    const lat1 = parseNum(obj.lat);
    const lng1 = parseNum(obj.lng) ?? parseNum(obj.lon);
    if (lat1 !== undefined && lng1 !== undefined) return { lat: lat1, lng: lng1 };

    const lat2 = parseNum(obj.latitude);
    const lng2 = parseNum(obj.longitude);
    if (lat2 !== undefined && lng2 !== undefined) return { lat: lat2, lng: lng2 };

    // coordinates array inside object
    if (Array.isArray(obj.coordinates) && obj.coordinates.length >= 2) {
      const a = obj.coordinates;
      const maybeLng = parseNum(a[0]);
      const maybeLat = parseNum(a[1]);
      if (maybeLat !== undefined && maybeLng !== undefined) return { lat: maybeLat, lng: maybeLng };
    }
    
    // string like "lat,lng" or "lng,lat"
    if (typeof obj === 'string' && obj.indexOf(',') !== -1) {
      const parts = obj.split(',').map(s => s.trim()).map(Number);
      if (parts.length >= 2 && parts.every(p => !Number.isNaN(p))) {
        if (Math.abs(parts[0]) <= 90) return { lat: parts[0], lng: parts[1] };
        return { lat: parts[1], lng: parts[0] };
      }
    }
    return undefined;
  };

  // check typical locations
  const candidates = [
    card, 
    card.base_payload, 
    card.custom_payload,
    card.base_payload?.location, 
    card.custom_payload?.location, 
    card.location, 
    card.address,
    card.base_payload?.geometry
  ];
  for (const c of candidates) {
    const r = tryObj(c);
    if (r) return r;
  }
  return undefined;
};

const CardListPage: React.FC<CardListPageProps> = (props) => {
  const { t } = useTravelI18n();
  const {
    initialTagFilters: initialTagFiltersProp,
    selectedTypes: selectedTypesProp,
    selectedRegions: selectedRegionsProp,
    sortBy: sortByProp = 'created_at',
    viewMode: viewModeProp = 'grid',
    title: titleProp = t('cardList.title'),
    embedded = false,
    onSelectCard,
    openDetailInModal = false,
    showAddButton = false,
  } = props;

  const initialTagFilters = useMemo(() => initialTagFiltersProp ?? [], [initialTagFiltersProp]);
  const selectedTypes = useMemo(() => selectedTypesProp ?? [], [selectedTypesProp]);
  const selectedRegions = useMemo(() => selectedRegionsProp ?? [], [selectedRegionsProp]);
  const sortBy = sortByProp;
  const viewMode = viewModeProp;
  const title = titleProp;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [showCityFilter, setShowCityFilter] = useState(false);

  // Normalize city strings to avoid mismatches (trim + unicode normalization)
  const normalizeCity = (input?: string) => {
    if (!input || typeof input !== 'string') return input;
    try { return input.trim().normalize ? input.trim().normalize('NFKC') : input.trim(); } catch (e) { return input.trim(); }
  };

  const [activeTagFilters, setActiveTagFilters] = useState<TagFilter[]>(initialTagFilters);
  const [localSelectedTypes, setLocalSelectedTypes] = useState<string[]>(selectedTypes);
  const [currentViewMode, setCurrentViewMode] = useState(viewMode);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [maxVisiblePages, setMaxVisiblePages] = useState(6);
  const [filtersAnchorY, setFiltersAnchorY] = useState(0);
  const [paging, setPaging] = useState(false);
  const FILTER_SCROLL_OFFSET = 36;
  const listTopRef = useRef<any>(null);
  const moduleRef = useRef<any>(null);
  const lastRequestIdRef = useRef(0);

  // 详情 Modal 控制
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailCard, setDetailCard] = useState<Card | null>(null);

  const alignViewportToFilters = useCallback(() => {
    const desiredOffset = Math.max(filtersAnchorY + FILTER_SCROLL_OFFSET, 0);

    // Embedded mode lives inside the homepage layout; scroll the main window to the component anchor.
    if (embedded && typeof window !== 'undefined') {
      const host: any = moduleRef.current;
      const rect = host && typeof host.getBoundingClientRect === 'function' ? host.getBoundingClientRect() : null;
      if (rect) {
        // For embedded mode, scroll to the top of the component (Title) instead of filters
        // User requested to scroll to "Recommended Cards" position
        const absoluteTop = window.scrollY + rect.top - 10;
        try {
          window.scrollTo({ top: absoluteTop, behavior: 'smooth' });
        } catch (err) {
          try { window.scrollTo(0, absoluteTop); } catch {}
        }
        return;
      }
    }

    const node = listTopRef.current;
    if (node) {
      try {
        node.scrollTo?.({ y: desiredOffset, animated: true });
      } catch (err) {
        try { node.scrollTo?.(0, desiredOffset); } catch {}
      }
      return;
    }

    if (typeof window !== 'undefined') {
      try { window.scrollTo({ top: desiredOffset, behavior: 'smooth' }); }
      catch { try { window.scrollTo(0, desiredOffset); } catch {} }
    }
  }, [FILTER_SCROLL_OFFSET, embedded, filtersAnchorY]);

  useEffect(() => {
    let active = true;
    const loadUser = async () => {
      try {
        if (supabaseClient?.auth?.getUser) {
          const { data } = await supabaseClient.auth.getUser();
          if (active) setCurrentUserId(data?.user?.id ?? null);
        }
      } catch (err) {
        if (active) setCurrentUserId(null);
      }
    };
    loadUser();

    let subscription: { unsubscribe: () => void } | undefined;
    if (supabaseClient?.auth?.onAuthStateChange) {
      const { data } = supabaseClient.auth.onAuthStateChange((_event, session) => {
        if (!active) return;
        setCurrentUserId(session?.user?.id ?? null);
      });
      subscription = data?.subscription;
    }

    return () => {
      active = false;
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  // 创建稳定的 URL 参数键，避免 searchParams 对象引用变化导致无限循环
  const urlParamsKey = useMemo(() => {
    const keys = ['tags', 'category', 'subcategory', 'country', 'province', 'city', 'district'];
    return keys.map(k => `${k}=${searchParams.get(k) || ''}`).join('&');
  }, [searchParams]);

  const urlTagFilters = useMemo(() => {
    const filters: TagFilter[] = [];
    const tagsParam = searchParams.get('tags');
    if (tagsParam) {
      const tagValues = tagsParam.split(',').map(tag => decodeURIComponent(tag.trim())).filter(Boolean);
      if (tagValues.length > 0) filters.push({ type: 'tags', values: tagValues, label: t('cardList.filters.tags') });
    }
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      const categoryValues = categoryParam.split(',').map(c => decodeURIComponent(c.trim())).filter(Boolean);
      if (categoryValues.length > 0) filters.push({ type: 'category', values: categoryValues, label: t('cardList.filters.category') });
    }
    const subcategoryParam = searchParams.get('subcategory');
    if (subcategoryParam) {
      const subcategoryValues = subcategoryParam.split(',').map(s => decodeURIComponent(s.trim())).filter(Boolean);
      if (subcategoryValues.length > 0) filters.push({ type: 'subcategory', values: subcategoryValues, label: t('cardList.filters.subcategory') });
    }
    // 读取层级地区过滤参数
    // 支持从 URL 读取 country, province, city, district 参数
    const country = searchParams.get('country');
    const province = searchParams.get('province');
    const city = searchParams.get('city');
    const district = searchParams.get('district');
    
    if (country || province || city || district) {
      // 构建层级地区过滤的显示值
      const locationParts = [country, province, city, district].filter(Boolean).map(v => decodeURIComponent(v!));
      if (locationParts.length > 0) {
        filters.push({ type: 'location', values: locationParts, label: t('cardList.filters.location') });
      }
    }
    return filters;
  }, [urlParamsKey, t]);

  // 读取层级地区过滤条件
  const urlLocationFilters = useMemo(() => {
    return {
      country: searchParams.get('country') ? decodeURIComponent(searchParams.get('country')!) : undefined,
      province: searchParams.get('province') ? decodeURIComponent(searchParams.get('province')!) : undefined,
      city: searchParams.get('city') ? decodeURIComponent(searchParams.get('city')!) : undefined,
      district: searchParams.get('district') ? decodeURIComponent(searchParams.get('district')!) : undefined,
    };
  }, [urlParamsKey]);

  // 记住 URL 中的 locationType（决定过滤哪个 JSON 字段）- 保留用于向后兼容
  const urlLocationType = useMemo(() => {
    const t = searchParams.get('locationType');
    return t ? decodeURIComponent(t).trim().toLowerCase() : undefined;
  }, [urlParamsKey]);

  // 直接在查询中使用合并后的过滤器，不通过状态
  const allFilters = useMemo(() => {
    return [...initialTagFilters, ...urlTagFilters];
  }, [initialTagFilters, urlTagFilters]);

  // 使用序列化的 allFilters 值来避免数组引用变化导致的无限循环
  const allFiltersKey = useMemo(() => JSON.stringify(allFilters), [allFilters]);
  
  // 创建稳定的 location filters 键
  const locationFiltersKey = useMemo(() => {
    return `${urlLocationFilters.country || ''}|${urlLocationFilters.province || ''}|${urlLocationFilters.city || ''}|${urlLocationFilters.district || ''}`;
  }, [urlLocationFilters.country, urlLocationFilters.province, urlLocationFilters.city, urlLocationFilters.district]);

  // 使用 ref 存储最新的过滤器值，避免 useCallback 依赖问题
  const filtersRef = useRef({ allFilters, searchQuery, localSelectedTypes, sortBy, urlLocationFilters, currentUserId, selectedCities });
  useEffect(() => {
    filtersRef.current = { allFilters, searchQuery, localSelectedTypes, sortBy, urlLocationFilters, currentUserId, selectedCities };
  }, [allFilters, searchQuery, localSelectedTypes, sortBy, locationFiltersKey, urlLocationFilters, currentUserId, selectedCities]);

  const cardTypes = [
    { key: 'poi', label: t('cardList.types.poi'), icon: 'place' },
    { key: 'restaurant', label: t('cardList.types.restaurant'), icon: 'restaurant' },
    { key: 'lodging', label: t('cardList.types.lodging'), icon: 'hotel' },
    { key: 'activity', label: t('cardList.types.activity'), icon: 'event' },
    { key: 'shopping', label: t('cardList.types.shopping'), icon: 'shopping-cart' },
  ];

  const applyVisibilityFilter = (query: any, userId?: string | null) => {
    if (userId) {
      const safeUserId = String(userId).replace(/[(),]/g, '');
      return query.or(`visibility.eq.public,and(visibility.eq.private,creator_id.eq.${safeUserId})`);
    }
    return query.eq('visibility', 'public');
  };

  const fetchAvailableOptions = async () => {
    try {
      let query: any = supabaseClient
        .from('cards_cards')
        .select('tags')
        .eq('status', 'active');
      query = applyVisibilityFilter(query, currentUserId);
      const { data, error } = await query;
      if (!error && data) {
        const allTags = new Set<string>();
        data.forEach((card: any) => {
          if (Array.isArray(card.tags)) card.tags.forEach((t: string) => allTags.add(t));
        });
        setAvailableTags(Array.from(allTags).sort());
      }
    } catch (e) {
      console.error('Failed to fetch available options:', e);
    }
  };

  // Fetch available cities from location field
  const fetchAvailableCities = async () => {
    try {
      let query: any = supabaseClient
        .from('cards_cards')
        .select('location')
        .eq('status', 'active');
      query = applyVisibilityFilter(query, currentUserId);
      const { data, error } = await query;
      if (!error && data) {
        const citiesSet = new Set<string>();
        data.forEach((card: any) => {
          if (card.location && typeof card.location === 'object' && card.location.city) {
            citiesSet.add(normalizeCity(card.location.city) as string);
          }
        });
        setAvailableCities(Array.from(citiesSet).sort());
      }
    } catch (e) {
      console.error('Failed to fetch available cities:', e);
    }
  };

  const fetchCards = useCallback(async (pageNum = 1, reset = false, showLoading = true, options?: { alignFilters?: boolean }) => {
    const requestId = ++lastRequestIdRef.current;
    if (options?.alignFilters) setPaging(true);
    try {
      // 从 ref 中获取最新的过滤器值
  const { allFilters, searchQuery, localSelectedTypes, sortBy, urlLocationFilters, currentUserId, selectedCities } = filtersRef.current;
      const normalizedSelectedCities = Array.isArray(selectedCities) ? selectedCities.map((c: string) => normalizeCity(c)) : [];
      const normalizedUrlLocationFilters = {
        country: normalizeCity(urlLocationFilters.country),
        province: normalizeCity(urlLocationFilters.province),
        city: normalizeCity(urlLocationFilters.city),
        district: normalizeCity(urlLocationFilters.district),
      };
      if (showLoading) setLoading(true);
      const limit = 12;

      const expandLocationValues = (vals: string[]) => {
        const set = new Set<string>();
        vals.forEach(v => {
          const s = String(v).trim();
          if (!s) return;
          set.add(s);
          // 简单同义词：澳门
          if (/^(澳門|澳门)$/i.test(s)) { set.add('澳門'); set.add('澳门'); set.add('Macau'); set.add('Macao'); }
          if (/^(macau|macao)$/i.test(s)) { set.add('澳門'); set.add('澳门'); set.add('Macau'); set.add('Macao'); }
        });
        return Array.from(set);
      };

  let countQuery: any = supabaseClient.from('cards_cards').select('id', { count: 'exact' }).eq('status', 'active');
  countQuery = applyVisibilityFilter(countQuery, currentUserId);
      if (searchQuery.trim()) countQuery = countQuery.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,address.ilike.%${searchQuery}%`);
      if (localSelectedTypes.length > 0) countQuery = countQuery.in('card_type', localSelectedTypes);
      
      // 应用层级地区过滤
      if (normalizedUrlLocationFilters.country) {
        countQuery = countQuery.eq('location->>country', normalizedUrlLocationFilters.country);
      }
      if (normalizedUrlLocationFilters.province) {
        countQuery = countQuery.eq('location->>province', normalizedUrlLocationFilters.province);
      }
      if (normalizedUrlLocationFilters.city) {
        countQuery = countQuery.eq('location->>city', normalizedUrlLocationFilters.city);
      }
      if (normalizedUrlLocationFilters.district) {
        countQuery = countQuery.eq('location->>district', normalizedUrlLocationFilters.district);
      }
      
      // Apply city filter from UI
      if (normalizedSelectedCities && normalizedSelectedCities.length > 0) {
        const cityConditions = normalizedSelectedCities.map((city: string) => `location->>city.eq.${city}`).join(',');
        countQuery = countQuery.or(cityConditions);
      }
      
      allFilters.forEach(filter => {
        if (filter.values.length > 0) {
          if (filter.type === 'tags') {
            console.log('Applying tags filter:', filter.values);
            // 使用最简单的方式：filter 配合 @> 操作符（数组包含）
            // 对每个标签单独过滤（AND 逻辑）
            filter.values.forEach(tag => {
              countQuery = countQuery.filter('tags', 'cs', `{${tag}}`);
              console.log(`Applied filter: tags cs {${tag}}`);
            });
          }
          if (filter.type === 'category') countQuery = countQuery.in('category', filter.values);
          if (filter.type === 'subcategory') countQuery = countQuery.in('subcategory', filter.values);
        }
      });

      const { count: cnt, error: countError } = await countQuery;
      if (countError) console.error('Count error:', countError);
      const total = typeof cnt === 'number' ? cnt : 0;
      setTotalCount(total);
      setTotalPages(total > 0 ? Math.ceil(total / limit) : 0);

      let query: any = supabaseClient
        .from('cards_cards')
        .select('id, title, description, address, card_type, tags, category, subcategory, cover_image, base_payload, created_at, creator_id, visibility, status, latitude, longitude, location')
        .eq('status', 'active');
      query = applyVisibilityFilter(query, currentUserId);
      if (searchQuery.trim()) query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,address.ilike.%${searchQuery}%`);
      if (localSelectedTypes.length > 0) query = query.in('card_type', localSelectedTypes);
      
      // 应用层级地区过滤
      if (normalizedUrlLocationFilters.country) {
        query = query.eq('location->>country', normalizedUrlLocationFilters.country);
      }
      if (normalizedUrlLocationFilters.province) {
        query = query.eq('location->>province', normalizedUrlLocationFilters.province);
      }
      if (normalizedUrlLocationFilters.city) {
        query = query.eq('location->>city', normalizedUrlLocationFilters.city);
      }
      if (normalizedUrlLocationFilters.district) {
        query = query.eq('location->>district', normalizedUrlLocationFilters.district);
      }
      
      // Apply city filter from UI
      if (normalizedSelectedCities && normalizedSelectedCities.length > 0) {
        const cityConditions = normalizedSelectedCities.map((city: string) => `location->>city.eq.${city}`).join(',');
        query = query.or(cityConditions);
      }
      
      allFilters.forEach(filter => {
        if (filter.values.length > 0) {
          if (filter.type === 'tags') {
            console.log('Applying tags filter to main query:', filter.values);
            // 使用最简单的方式：filter 配合 @> 操作符（数组包含）
            filter.values.forEach(tag => {
              query = query.filter('tags', 'cs', `{${tag}}`);
              console.log(`Applied filter to main query: tags cs {${tag}}`);
            });
          }
          if (filter.type === 'category') query = query.in('category', filter.values);
          if (filter.type === 'subcategory') query = query.in('subcategory', filter.values);
        }
      });

      switch (sortBy) {
        case 'created_at':
          query = query.order('created_at', { ascending: false });
          break;
        case 'popularity':
          query = query.order('created_at', { ascending: false });
          break;
        case 'rating':
          query = query.order('created_at', { ascending: false });
          break;
      }

      const offset = (pageNum - 1) * limit;
      query = query.range(offset, offset + limit - 1);
      const { data, error } = await query;
      
      if (requestId !== lastRequestIdRef.current) return;

      if (!error && data) {
        console.log('Query returned data:', data.length, 'cards');
        console.log('First 3 cards tags:', data.slice(0, 3).map(c => ({ title: c.title, tags: c.tags })));
        if (reset || pageNum === 1) setCards(data);
        else setCards(prev => [...prev, ...data]);
        setHasMore(data.length === limit);
        if (options?.alignFilters) alignViewportToFilters();
      } else {
        console.error('Failed to fetch cards:', error);
      }
    } catch (e) {
      console.error('Error fetching cards:', e);
    } finally {
      // always ensure loading flag is false to avoid sticky spinner
      if (requestId === lastRequestIdRef.current) {
        setLoading(false);
        if (options?.alignFilters) setPaging(false);
      }
    }
  }, [alignViewportToFilters]); // other inputs read via refs/helpers

  // 同步 URL 过滤器到 activeTagFilters（仅用于 UI 显示）
  useEffect(() => {
    if (urlTagFilters.length > 0 || initialTagFilters.length > 0) {
      setActiveTagFilters([...initialTagFilters, ...urlTagFilters]);
    } else {
      setActiveTagFilters([]);
    }
  }, [urlTagFilters, initialTagFilters]);

  // 当过滤条件变化时，重置页码并重新获取数据
  useEffect(() => {
    console.log('Filters changed:', { searchQuery, allFilters, localSelectedTypes, sortBy, urlLocationFilters, currentUserId, selectedCities });
    fetchAvailableOptions();
    fetchAvailableCities();
    fetchCards(1, true); // 直接传 1，不需要 setPage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, localSelectedTypes, sortBy, locationFiltersKey, allFiltersKey, currentUserId, selectedCities]);

  // Responsive max visible page numbers based on window width
  useEffect(() => {
    const calculate = () => {
      if (typeof window === 'undefined') return;
      const w = window.innerWidth;
      if (w < 480) setMaxVisiblePages(3);
      else if (w < 900) setMaxVisiblePages(5);
      else setMaxVisiblePages(6);
    };
    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, []);

  const handleTagFilterToggle = (type: TagFilter['type'], value: string, label: string) => {
    setActiveTagFilters(prev => {
      const existingFilter = prev.find(f => f.type === type && f.values.includes(value));
      if (existingFilter) return [];
      return [{ type, values: [value], label }];
    });
    setPage(1);
  };

  // 移除某个过滤值（用于"地区"chips 的 X 按钮）
  const handleRemoveFilterValue = (type: TagFilter['type'], value: string) => {
    // 如果移除的是地区过滤值，需要同时移除下级的标签
    if (type === 'location') {
      const newParams = new URLSearchParams(searchParams);
      
      // 检查移除的是哪个层级的值，并收集需要移除的所有值（包括下级）
      const valuesToRemove = new Set<string>([value]);
      
      if (newParams.get('district') === value) {
        // 移除区级，只需移除这一个
        newParams.delete('district');
      } else if (newParams.get('city') === value) {
        // 移除市级，同时移除区级
        newParams.delete('city');
        const districtValue = newParams.get('district');
        if (districtValue) {
          valuesToRemove.add(districtValue);
          newParams.delete('district');
        }
      } else if (newParams.get('province') === value) {
        // 移除省级，同时移除市级和区级
        newParams.delete('province');
        const cityValue = newParams.get('city');
        const districtValue = newParams.get('district');
        if (cityValue) valuesToRemove.add(cityValue);
        if (districtValue) valuesToRemove.add(districtValue);
        newParams.delete('city');
        newParams.delete('district');
      } else if (newParams.get('country') === value) {
        // 移除国家级，同时移除所有下级
        newParams.delete('country');
        const provinceValue = newParams.get('province');
        const cityValue = newParams.get('city');
        const districtValue = newParams.get('district');
        if (provinceValue) valuesToRemove.add(provinceValue);
        if (cityValue) valuesToRemove.add(cityValue);
        if (districtValue) valuesToRemove.add(districtValue);
        newParams.delete('province');
        newParams.delete('city');
        newParams.delete('district');
      }
      
      // 更新 activeTagFilters，移除当前值和所有下级值
      setActiveTagFilters(prev => {
        const next = prev.map(f => 
          f.type === type 
            ? { ...f, values: f.values.filter(v => !valuesToRemove.has(v)) } 
            : f
        ).filter(f => f.values.length > 0);
        return next;
      });
      
      setPage(1);
      navigate(`/cards?${newParams.toString()}`, { replace: true });
    } else if (type === 'tags') {
      // 移除标签：更新 URL 和状态
      const newParams = new URLSearchParams(searchParams);
      const currentTags = newParams.get('tags');
      if (currentTags) {
        const tagsArray = currentTags.split(',').map(t => decodeURIComponent(t.trim())).filter(t => t !== value);
        if (tagsArray.length > 0) {
          newParams.set('tags', tagsArray.map(t => encodeURIComponent(t)).join(','));
        } else {
          newParams.delete('tags');
        }
      }
      
      setActiveTagFilters(prev => {
        const next = prev.map(f => f.type === type ? { ...f, values: f.values.filter(v => v !== value) } : f)
                        .filter(f => f.values.length > 0);
        return next;
      });
      
      setPage(1);
      navigate(`/cards?${newParams.toString()}`, { replace: true });
    } else {
      // 非地区/标签过滤，保持原逻辑
      setActiveTagFilters(prev => {
        const next = prev.map(f => f.type === type ? { ...f, values: f.values.filter(v => v !== value) } : f)
                        .filter(f => f.values.length > 0);
        return next;
      });
      setPage(1);
    }
  };

  // 清空某一类过滤（例如全部"地区"）
  const handleClearFilterType = (type: TagFilter['type']) => {
    setActiveTagFilters(prev => prev.filter(f => f.type !== type));
    setPage(1);
    
    // 如果清除的是地区过滤，同时清除 URL 中的地区参数
    if (type === 'location') {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('country');
      newParams.delete('province');
      newParams.delete('city');
      newParams.delete('district');
      newParams.delete('locationType'); // 也清除旧的参数
      newParams.delete('location'); // 也清除旧的参数
      
      // 更新 URL
      navigate(`/cards?${newParams.toString()}`, { replace: true });
    } else if (type === 'tags') {
      // 清除标签过滤
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tags');
      
      // 更新 URL
      navigate(`/cards?${newParams.toString()}`, { replace: true });
    }
  };

  const handleTypeToggle = (type: string) => {
    console.log('Type toggle clicked:', type);
    setLocalSelectedTypes(prev => {
      const newTypes = prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type];
      console.log('New selected types:', newTypes);
      return newTypes;
    });
  };

  const handleCardPress = (card: Card) => {
    if (openDetailInModal) {
      setDetailCard(card);
      setDetailModalVisible(true);
      return;
    }
    navigate(`/card/${card.id}`);
  };
  const loadMore = () => { if (!loading && hasMore) { const nextPage = page + 1; setPage(nextPage); fetchCards(nextPage, false); } };

  // produce a consecutive window of pages centered on current page
  const getPageWindow = (total: number, current: number) => {
    const maxVisible = Math.max(1, Math.min(total, maxVisiblePages || 5));
    if (total <= maxVisible) return Array.from({ length: total }).map((_, i) => i + 1);
    let start = current - Math.floor(maxVisible / 2);
    if (start < 1) start = 1;
    let end = start + maxVisible - 1;
    if (end > total) {
      end = total;
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }).map((_, i) => start + i);
  };

  const renderCard = (card: Card) => {
    const isGridMode = currentViewMode === 'grid';
    const isPrivateOwned = card.visibility === 'private' && card.creator_id === currentUserId;
    const isVideoCard = card.card_type === 'video' || !!card.base_payload?.embed;
    const PLACEHOLDER_IMG = '/assets/placeholders/card-header-default.jpg';
    const pickCover = (c: Card): string | undefined => {
      try {
        // 視頻卡：優先使用 base_payload 裏面的縮略圖
        if (isVideoCard) {
          const base: any = (c as any).base_payload || {};
          const embed: any = base.embed || {};
          const videoThumb = base.imageUrl || embed.poster;
          if (videoThumb) return String(videoThumb);
        }

        const url = (c as any).cover_image || (c as any).imageUrl || (c as any).image_url || (c as any).image || '';
        if (!url) return undefined;
        const s = String(url);
        // Strip unreliable static map provider urls
        if (/staticmap\.openstreetmap\.de|staticmap\.php/i.test(s)) return PLACEHOLDER_IMG;
        return s;
      } catch {
        return undefined;
      }
    };
    const coverUrl = pickCover(card);
    const coords = extractCoords(card);
    const mapHeight = isGridMode ? '100%' : 100;
    
    return (
      <TouchableOpacity key={card.id} style={[styles.cardItem, isGridMode ? styles.cardItemGrid : styles.cardItemList]} onPress={() => handleCardPress(card)}>
        <View style={[styles.cardCover, isGridMode ? styles.cardCoverGrid : styles.cardCoverList]}>
          {coverUrl ? (
            <Image source={{ uri: coverUrl }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
          ) : coords ? (
            <View style={{ width: '100%', height: '100%', overflow: 'hidden' }} pointerEvents="none">
              <NearbyMap
                key={`map-${card.id}`}
                center={[coords.lat, coords.lng]}
                spots={[]}
                primaryLabel={card.title}
                primaryName={card.title}
                enableOSM={false}
                showInterPointDistances={false}
                height={mapHeight}
                showFullscreenButton={false}
                showControls={false}
                embedded={true}
              />
            </View>
          ) : (
            <View style={styles.cardImagePlaceholder}><PlatformIcon name="image" size={24} color="#9CA3AF" /></View>
          )}
          {/* 类型徽章 */}
          <View style={styles.cardTypeBadge}><PlatformIcon name={cardTypes.find(t => t.key === card.card_type)?.icon || 'place'} size={12} color="#fff" /></View>
          {isPrivateOwned && (
            <View style={styles.privateBadge}>
              <PlatformIcon name="lock" size={12} color="#fff" family="MaterialIcons" />
            </View>
          )}

          {/* 加入行程悬浮按钮（可选） */}
          {onSelectCard && showAddButton && (
            <TouchableOpacity
              accessibilityLabel="加入行程"
              accessibilityRole="button"
              style={styles.addFloatBtn}
              activeOpacity={0.9}
              onPress={(ev?: any) => {
                try { if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation(); } catch (e) {}
                onSelectCard(card);
              }}
            >
              <PlatformIcon name="add" size={16} color="#fff" />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.cardInfo}>
          <Text style={styles.cardTitle} numberOfLines={2}>{card.title || '未命名卡片'}</Text>
          {card.address && (<View style={styles.cardAddress}><MdLocationOn size={14} color="#6B7280" /><Text style={styles.cardAddressText} numberOfLines={1}>{card.address}</Text></View>)}
          <View style={styles.cardTagsContainer}>{Array.isArray(card.tags) && card.tags.length > 0 && (<View style={styles.cardTags}>{card.tags.slice(0, 2).map((tag, i) => (<View key={i} style={styles.cardTag}><Text style={styles.cardTagText}>#{tag}</Text></View>))}{card.tags.length > 2 && <Text style={styles.cardTagMore}>+{card.tags.length - 2}</Text>}</View>)}</View>
        </View>

        {/* 右侧扁平纵向 Pill 按钮（仅横向布局时显示），复制 CardPicker 样式 */}
        {(!isGridMode && (onSelectCard || showAddButton)) && (
          <View style={[styles.listAddWrap, { pointerEvents: 'box-none' }]}>
            <TouchableOpacity
              accessibilityLabel="加入行程"
              accessibilityRole="button"
              onPress={(ev?: any) => { 
                try { if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation(); } catch (e) {}
                if (onSelectCard) onSelectCard(card); else navigate(`/card/${card.id}`);
              }}
              activeOpacity={0.9}
              style={styles.listAddPill}
            >
              <PlatformIcon name="add" size={16} color="#FFFFFF" />
            </TouchableOpacity>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <View ref={moduleRef} style={styles.container}>
      <View style={!embedded ? styles.inner : undefined}>
      {/* 关闭按钮 - 仅在非嵌入模式显示 */}
      {!embedded && (
        <View style={styles.closeButtonRow}>
          <TouchableOpacity onPress={() => navigate(-1)} style={styles.closeButton}>
            <MdClose size={28} color="#333" />
          </TouchableOpacity>
        </View>
      )}
      
      <View style={[styles.header, embedded && styles.headerEmbedded]}>
        <View style={styles.titleRow}>
          <View style={styles.titleBar} />
          <Text style={[styles.title, embedded && styles.titleEmbedded]}>{title}</Text>
        </View>
        <View style={[styles.viewModeToggle, embedded && styles.viewModeToggleEmbedded]}>
          <TouchableOpacity style={[styles.viewModeBtn, embedded && styles.viewModeBtnEmbedded, currentViewMode === 'grid' && styles.viewModeBtnActive]} onPress={() => setCurrentViewMode('grid')}>
            <PlatformIcon name="grid_view" size={embedded ? 14 : 20} color={currentViewMode === 'grid' ? '#0B61D6' : '#6B7280'} />
          </TouchableOpacity>
          <TouchableOpacity style={[styles.viewModeBtn, embedded && styles.viewModeBtnEmbedded, currentViewMode === 'list' && styles.viewModeBtnActive]} onPress={() => setCurrentViewMode('list')}>
            <PlatformIcon name="view_list" size={embedded ? 14 : 20} color={currentViewMode === 'list' ? '#0B61D6' : '#6B7280'} />
          </TouchableOpacity>
        </View>
      </View>

      {embedded ? (
        <>
          <View style={styles.searchContainer}>
            <View style={styles.searchRow}>
              <View style={styles.searchBox}><PlatformIcon name="search" size={20} color="#6B7280" /><TextInput style={styles.searchInput} placeholder={t('cardList.searchPlaceholder')} value={searchQuery} onChangeText={setSearchQuery} /></View>
              <TouchableOpacity 
                style={[styles.filterButton, selectedCities.length > 0 && styles.filterButtonActive]}
                onPress={() => setShowCityFilter(!showCityFilter)}
                accessibilityLabel="城市过滤"
              >
                <PlatformIcon name="tune" size={20} color={selectedCities.length > 0 ? '#0B61D6' : '#6B7280'} family="MaterialIcons" />
                {selectedCities.length > 0 && (
                  <View style={styles.filterBadge}>
                    <Text style={styles.filterBadgeText}>{selectedCities.length}</Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>
            {showCityFilter && (
              <View style={styles.cityFilterContainer}>
                <View style={styles.cityFilterHeader}>
                  <TouchableOpacity onPress={() => setShowCityFilter(false)}>
                    <PlatformIcon name="close" size={20} color="#6B7280" family="MaterialIcons" />
                  </TouchableOpacity>
                </View>
                <View style={styles.cityChipsWrap}>
                  {availableCities.map((city) => {
                    const isSelected = selectedCities.includes(city);
                    return (
                      <TouchableOpacity
                        key={city}
                        style={[styles.cityChip, isSelected && styles.cityChipSelected]}
                        onPress={() => {
                          setSelectedCities(prev => 
                            isSelected ? prev.filter(c => c !== city) : [...prev, city]
                          );
                        }}
                      >
                        <Text style={[styles.cityChipText, isSelected && styles.cityChipTextSelected]}>
                          {city}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            )}
          </View>

          <View onLayout={(e) => setFiltersAnchorY(e.nativeEvent.layout.y)}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersContainer}>
              <View style={styles.filterGroup}>
                {cardTypes.map(type => (
                  <TouchableOpacity key={type.key} style={[styles.filterChip, localSelectedTypes.includes(type.key) && styles.filterChipActive]} onPress={() => handleTypeToggle(type.key)}>
                    <PlatformIcon name={type.icon} size={16} color={localSelectedTypes.includes(type.key) ? '#0B61D6' : '#6B7280'} />
                    <Text style={[styles.filterChipText, localSelectedTypes.includes(type.key) && styles.filterChipTextActive]}>{type.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>

          {/* 标签过滤条：显示在类型筛选下方，可清除 */}
          {activeTagFilters.some(f => f.type === 'tags' && f.values.length > 0) && (
            <View style={styles.activeFiltersBar}>
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                {activeTagFilters.find(f => f.type === 'tags')!.values.map((v, i) => (
                  <View key={`tag-${v}-${i}`} style={styles.activeFilterChip}>
                    <Text style={styles.activeFilterChipText}># {v}</Text>
                    <TouchableOpacity onPress={() => handleRemoveFilterValue('tags', v)} style={styles.activeFilterClose}>
                      <PlatformIcon name="close" size={12} color="#6B7280" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
              <TouchableOpacity onPress={() => handleClearFilterType('tags')} style={styles.clearAllBtn}>
                <Text style={styles.clearAllText}>{t('cardList.filters.clear')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* 地区过滤条：显示在类型筛选下方，可清除 */}
          {activeTagFilters.some(f => f.type === 'location' && f.values.length > 0) && (
            <View style={styles.activeFiltersBar}>
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                {activeTagFilters.find(f => f.type === 'location')!.values.map((v, i) => (
                  <View key={`${v}-${i}`} style={styles.activeFilterChip}>
                    <Text style={styles.activeFilterChipText}># {v}</Text>
                    <TouchableOpacity onPress={() => handleRemoveFilterValue('location', v)} style={styles.activeFilterClose}>
                      <PlatformIcon name="close" size={12} color="#6B7280" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
              <TouchableOpacity onPress={() => handleClearFilterType('location')} style={styles.clearAllBtn}>
                <Text style={styles.clearAllText}>{t('cardList.filters.clear')}</Text>
              </TouchableOpacity>
            </View>
          )}

          <ScrollView ref={listTopRef} style={styles.content} onScroll={({ nativeEvent }) => { const { layoutMeasurement, contentOffset, contentSize } = nativeEvent; const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 20; if (isCloseToBottom) loadMore(); }} scrollEventThrottle={400}>
            <View style={{ position: 'relative' }}>
              <View style={[styles.cardsContainer, currentViewMode === 'grid' ? styles.cardsContainerGrid : styles.cardsContainerList]}>{cards.map(renderCard)}</View>
              {paging && (
                <View style={styles.pageTransitionOverlay} pointerEvents="none">
                  <ActivityIndicator size="small" color="#0B61D6" />
                  <Text style={styles.pageTransitionText}>{t('cardList.status.switchingPages')}</Text>
                </View>
              )}
            </View>

            {loading && (<View style={styles.loadingContainer}><ActivityIndicator size="small" color="#0B61D6" /><Text style={styles.loadingText}>{t('cardList.status.loading')}</Text></View>)}
            {!loading && !hasMore && cards.length > 0 && (<View style={styles.noMoreContainer}><Text style={styles.noMoreText}>{t('cardList.status.noMore')}</Text></View>)}
            {!loading && cards.length === 0 && (<View style={styles.emptyContainer}><PlatformIcon name="search_off" size={48} color="#9CA3AF" /><Text style={styles.emptyTitle}>{t('cardList.status.emptyTitle')}</Text><Text style={styles.emptyText}>{t('cardList.status.emptyText')}</Text></View>)}

            {totalPages > 1 && (
              <View style={styles.paginationContainer}>
                <TouchableOpacity style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]} onPress={() => { if (page === 1) return; setPage(1); fetchCards(1, true, false, { alignFilters: true }); }}>
                  <Text style={styles.pageButtonText}>«</Text>
                </TouchableOpacity>

                <TouchableOpacity style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]} onPress={() => { if (page === 1) return; const p = Math.max(1, page - 1); setPage(p); fetchCards(p, true, false, { alignFilters: true }); }}>
                  <Text style={styles.pageButtonText}>‹</Text>
                </TouchableOpacity>

                {getPageWindow(totalPages, page).map(p => (
                  <TouchableOpacity key={p} style={[styles.pageButton, page === p && styles.pageButtonActive]} onPress={() => { if (p === page) return; setPage(p); fetchCards(p, true, false, { alignFilters: true }); }}>
                    <Text style={[styles.pageButtonText, page === p && styles.pageButtonTextActive]}>{p}</Text>
                  </TouchableOpacity>
                ))}

                <TouchableOpacity style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]} onPress={() => { if (page === totalPages) return; const p = Math.min(totalPages, page + 1); setPage(p); fetchCards(p, true, false, { alignFilters: true }); }}>
                  <Text style={styles.pageButtonText}>›</Text>
                </TouchableOpacity>

                <TouchableOpacity style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]} onPress={() => { if (page === totalPages) return; setPage(totalPages); fetchCards(totalPages, true, false, { alignFilters: true }); }}>
                  <Text style={styles.pageButtonText}>»</Text>
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </>
      ) : (
        <>
          <View style={styles.searchContainer}>
              <View style={styles.searchRow}>
                <View style={styles.searchBox}><PlatformIcon name="search" size={20} color="#6B7280" /><TextInput style={styles.searchInput} placeholder={t('cardList.searchPlaceholder')} value={searchQuery} onChangeText={setSearchQuery} /></View>
                <TouchableOpacity 
                  style={[styles.filterButton, selectedCities.length > 0 && styles.filterButtonActive]}
                  onPress={() => setShowCityFilter(!showCityFilter)}
                  accessibilityLabel="城市过滤"
                >
                  <PlatformIcon name="tune" size={20} color={selectedCities.length > 0 ? '#0B61D6' : '#6B7280'} family="MaterialIcons" />
                  {selectedCities.length > 0 && (
                    <View style={styles.filterBadge}>
                      <Text style={styles.filterBadgeText}>{selectedCities.length}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              </View>
              {showCityFilter && (
                <View style={styles.cityFilterContainer}>
                  <View style={styles.cityFilterHeader}>
                    <TouchableOpacity onPress={() => setShowCityFilter(false)}>
                      <PlatformIcon name="close" size={20} color="#6B7280" family="MaterialIcons" />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.cityChipsWrap}>
                    {availableCities.map((city) => {
                      const isSelected = selectedCities.includes(city);
                      return (
                        <TouchableOpacity
                          key={city}
                          style={[styles.cityChip, isSelected && styles.cityChipSelected]}
                          onPress={() => {
                            setSelectedCities(prev => 
                              isSelected ? prev.filter(c => c !== city) : [...prev, city]
                            );
                          }}
                        >
                          <Text style={[styles.cityChipText, isSelected && styles.cityChipTextSelected]}>
                            {city}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              )}
            </View>

          <View onLayout={(e) => setFiltersAnchorY(e.nativeEvent.layout.y)}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filtersContainer}>
              <View style={styles.filterGroup}>
                {cardTypes.map(type => (
                  <TouchableOpacity key={type.key} style={[styles.filterChip, localSelectedTypes.includes(type.key) && styles.filterChipActive]} onPress={() => handleTypeToggle(type.key)}>
                    <PlatformIcon name={type.icon} size={16} color={localSelectedTypes.includes(type.key) ? '#0B61D6' : '#6B7280'} />
                    <Text style={[styles.filterChipText, localSelectedTypes.includes(type.key) && styles.filterChipTextActive]}>{type.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>

          {/* 标签过滤条：显示在类型筛选下方，可清除 */}
          {activeTagFilters.some(f => f.type === 'tags' && f.values.length > 0) && (
            <View style={styles.activeFiltersBar}>
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                {activeTagFilters.find(f => f.type === 'tags')!.values.map((v, i) => (
                  <View key={`tag-${v}-${i}`} style={styles.activeFilterChip}>
                    <Text style={styles.activeFilterChipText}># {v}</Text>
                    <TouchableOpacity onPress={() => handleRemoveFilterValue('tags', v)} style={styles.activeFilterClose}>
                      <PlatformIcon name="close" size={12} color="#6B7280" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
              <TouchableOpacity onPress={() => handleClearFilterType('tags')} style={styles.clearAllBtn}>
                <Text style={styles.clearAllText}>{t('cardList.filters.clear')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {activeTagFilters.some(f => f.type === 'location' && f.values.length > 0) && (
            <View style={styles.activeFiltersBar}>
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
                {activeTagFilters.find(f => f.type === 'location')!.values.map((v, i) => (
                  <View key={`${v}-${i}`} style={styles.activeFilterChip}>
                    <Text style={styles.activeFilterChipText}># {v}</Text>
                    <TouchableOpacity onPress={() => handleRemoveFilterValue('location', v)} style={styles.activeFilterClose}>
                      <PlatformIcon name="close" size={12} color="#6B7280" />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
              <TouchableOpacity onPress={() => handleClearFilterType('location')} style={styles.clearAllBtn}>
                <Text style={styles.clearAllText}>{t('cardList.filters.clear')}</Text>
              </TouchableOpacity>
            </View>
          )}

          <ScrollView ref={listTopRef} style={styles.content} onScroll={({ nativeEvent }) => { const { layoutMeasurement, contentOffset, contentSize } = nativeEvent; const isCloseToBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 20; if (isCloseToBottom) loadMore(); }} scrollEventThrottle={400}>
            <View style={{ position: 'relative' }}>
              <View style={[styles.cardsContainer, currentViewMode === 'grid' ? styles.cardsContainerGrid : styles.cardsContainerList]}>{cards.map(renderCard)}</View>
              {paging && (
                <View style={styles.pageTransitionOverlay} pointerEvents="none">
                  <ActivityIndicator size="small" color="#0B61D6" />
                  <Text style={styles.pageTransitionText}>{t('cardList.status.switchingPages')}</Text>
                </View>
              )}
            </View>
            {loading && (<View style={styles.loadingContainer}><ActivityIndicator size="small" color="#0B61D6" /><Text style={styles.loadingText}>{t('cardList.status.loading')}</Text></View>)}
            {!loading && !hasMore && cards.length > 0 && (<View style={styles.noMoreContainer}><Text style={styles.noMoreText}>{t('cardList.status.noMore')}</Text></View>)}
            {!loading && cards.length === 0 && (<View style={styles.emptyContainer}><PlatformIcon name="search_off" size={48} color="#9CA3AF" /><Text style={styles.emptyTitle}>{t('cardList.status.emptyTitle')}</Text><Text style={styles.emptyText}>{t('cardList.status.emptyText')}</Text></View>)}

            {totalPages > 1 && (
              <View style={styles.paginationContainer}>
                <TouchableOpacity style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]} onPress={() => { if (page === 1) return; setPage(1); fetchCards(1, true, false, { alignFilters: true }); }}><Text style={styles.pageButtonText}>«</Text></TouchableOpacity>
                <TouchableOpacity style={[styles.pageButton, page === 1 && styles.pageButtonDisabled]} onPress={() => { if (page === 1) return; const p = Math.max(1, page - 1); setPage(p); fetchCards(p, true, false, { alignFilters: true }); }}><Text style={styles.pageButtonText}>‹</Text></TouchableOpacity>
                {getPageWindow(totalPages, page).map(p => (
                  <TouchableOpacity key={p} style={[styles.pageButton, page === p && styles.pageButtonActive]} onPress={() => { if (p === page) return; setPage(p); fetchCards(p, true, false, { alignFilters: true }); }}>
                    <Text style={[styles.pageButtonText, page === p && styles.pageButtonTextActive]}>{p}</Text>
                  </TouchableOpacity>
                ))}
                <TouchableOpacity style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]} onPress={() => { if (page === totalPages) return; const p = Math.min(totalPages, page + 1); setPage(p); fetchCards(p, true, false, { alignFilters: true }); }}><Text style={styles.pageButtonText}>›</Text></TouchableOpacity>
                <TouchableOpacity style={[styles.pageButton, page === totalPages && styles.pageButtonDisabled]} onPress={() => { if (page === totalPages) return; setPage(totalPages); fetchCards(totalPages, true, false, { alignFilters: true }); }}><Text style={styles.pageButtonText}>»</Text></TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </>
      )}
      </View>
      {/* 详情 Modal */}
      {openDetailInModal && (
        <Modal
          visible={detailModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setDetailModalVisible(false)}
        >
          <View style={styles.modalBackdrop}>
            <View style={styles.modalContent}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>{detailCard?.title || t('cardList.modal.title')}</Text>
                <TouchableOpacity onPress={() => setDetailModalVisible(false)} accessibilityLabel="关闭" accessibilityRole="button">
                  <PlatformIcon name="close" size={20} color="#6B7280" />
                </TouchableOpacity>
              </View>
              <ScrollView style={{ maxHeight: 480 }} contentContainerStyle={{ paddingBottom: 16 }}>
                <View style={styles.modalBody}>
                  <View style={styles.modalCover}>
                    {detailCard?.cover_image ? (
                      <Image source={{ uri: detailCard.cover_image }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                    ) : (
                      <View style={styles.cardImagePlaceholder}><PlatformIcon name="image" size={32} color="#9CA3AF" /></View>
                    )}
                  </View>
                  {detailCard?.address && (
                    <View style={styles.modalRow}>
                      <MdLocationOn size={16} color="#6B7280" />
                      <Text style={{ fontSize: 14, color: '#374151', marginLeft: 6, flex: 1 }} numberOfLines={2}>{detailCard.address}</Text>
                    </View>
                  )}
                  {detailCard?.description && (
                    <Text style={{ fontSize: 14, color: '#374151', marginTop: 12, lineHeight: 20 }}>{detailCard.description}</Text>
                  )}
                  {Array.isArray(detailCard?.tags) && detailCard!.tags!.length > 0 && (
                    <View style={[styles.modalRow, { flexWrap: 'wrap', marginTop: 12 }]}>
                      {detailCard!.tags!.map((t, i) => (
                        <View key={i} style={[styles.cardTag, { marginBottom: 4 }]}>
                          <Text style={styles.cardTagText}>#{t}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                  <View style={styles.modalActions}>
                    <TouchableOpacity
                      onPress={() => setDetailModalVisible(false)}
                      style={styles.modalBtn}
                      accessibilityLabel="关闭"
                      accessibilityRole="button"
                    >
                      <Text style={styles.modalBtnText}>{t('cardList.modal.close')}</Text>
                    </TouchableOpacity>
                    {onSelectCard && detailCard && (
                      <TouchableOpacity
                        onPress={() => { onSelectCard(detailCard); setDetailModalVisible(false); }}
                        style={[styles.modalBtn, styles.modalBtnPrimary]}
                        accessibilityLabel="加入行程"
                        accessibilityRole="button"
                      >
                        <Text style={[styles.modalBtnText, styles.modalBtnTextPrimary]}>{t('cardList.modal.addToItinerary')}</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff' },
  closeButtonRow: { paddingHorizontal: 24, paddingTop: 16, paddingBottom: 8 },
  closeButton: { padding: 4 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  title: { fontSize: 24, fontWeight: '800', color: '#111827' },
  titleEmbedded: { fontSize: 16, fontWeight: '700' },
  titleRow: { flexDirection: 'row', alignItems: 'center' },
  titleBar: { width: 4, height: 20, backgroundColor: '#ff6b35', borderRadius: 2, marginRight: 10 },
  headerEmbedded: { paddingVertical: 8, borderBottomWidth: 0, paddingHorizontal: 16 },
  viewModeToggle: { flexDirection: 'row', backgroundColor: '#F3F4F6', borderRadius: 8, padding: 2 },
  viewModeToggleEmbedded: { padding: 1, borderRadius: 8 },
  viewModeBtn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 6 },
  viewModeBtnEmbedded: { paddingHorizontal: 6, paddingVertical: 4 },
  viewModeBtnActive: { backgroundColor: '#fff' },

  searchContainer: { paddingHorizontal: 24, paddingVertical: 16 },
  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 20, paddingHorizontal: 16, paddingVertical: 8 },
  searchInput: { flex: 1, marginLeft: 12, fontSize: 16, color: '#111827', outlineWidth: 0, outlineColor: 'transparent', boxShadow: 'none' },
  filterButton: { width: 44, height: 44, borderRadius: 22, backgroundColor: '#F3F4F6', alignItems: 'center', justifyContent: 'center', position: 'relative' as any },
  filterButtonActive: { backgroundColor: '#E6F0FF' },
  filterBadge: { position: 'absolute' as any, top: -4, right: -4, backgroundColor: '#FB7A4A', borderRadius: 10, minWidth: 20, height: 20, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6 },
  filterBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700' as any },
  cityFilterContainer: { marginTop: 12, backgroundColor: '#F9FAFB', borderRadius: 12, padding: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  cityFilterHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 8 },
  cityChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  cityChip: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E5E7EB' },
  cityChipSelected: { backgroundColor: '#0B61D6', borderColor: '#0B61D6' },
  cityChipText: { fontSize: 14, color: '#374151', fontWeight: '500' as any },
  cityChipTextSelected: { color: '#fff', fontWeight: '600' as any },
  searchTagsContainer: { flexDirection: 'row', alignItems: 'center', marginLeft: 8 },
  searchTagChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, marginLeft: 4 },
  searchTagText: { fontSize: 12, color: '#6B7280', marginRight: 4 },

  filtersContainer: { paddingHorizontal: 24, marginBottom: 16 },
  filterGroup: { flexDirection: 'row', alignItems: 'center', marginRight: 16 },
  filterChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', paddingHorizontal: 12, paddingVertical: 8, borderRadius: 20, marginRight: 8 },
  filterChipActive: { backgroundColor: '#E6F0FF' },
  filterChipText: { fontSize: 14, color: '#6B7280', marginLeft: 4 },
  filterChipTextActive: { color: '#0B61D6', fontWeight: '600' },

  // Active filters (below type chips)
  activeFiltersBar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 24, marginTop: -8, marginBottom: 8 },
  activeFilterChip: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, marginRight: 8, marginTop: 8 },
  activeFilterChipText: { fontSize: 13, color: '#6B7280' },
  activeFilterClose: { marginLeft: 6 },
  clearAllBtn: { paddingHorizontal: 8, paddingVertical: 6, marginLeft: 6 },
  clearAllText: { fontSize: 13, color: '#9CA3AF' },

  content: { flex: 1 },
  inner: { maxWidth: 600, width: '100%', alignSelf: 'center' },
  cardsContainer: { paddingHorizontal: 24 },
  cardsContainerGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  cardsContainerList: { flexDirection: 'column' },
  pageTransitionOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.85)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  pageTransitionText: { marginTop: 8, fontSize: 13, color: '#4B5563' },

  cardItem: Platform.select({
    web: { backgroundColor: '#fff', borderRadius: 12, marginBottom: 16, boxShadow: '0px 2px 8px rgba(0,0,0,0.05)', position: 'relative' as any },
    default: { backgroundColor: '#fff', borderRadius: 12, marginBottom: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3, position: 'relative' as any }
  }),
  cardItemGrid: { width: '48%' },
  cardItemList: { width: '100%', flexDirection: 'row' },
  cardCover: { backgroundColor: '#F3F4F6', position: 'relative' },
  cardCoverGrid: { aspectRatio: 16 / 9, width: '100%', borderTopLeftRadius: 12, borderTopRightRadius: 12 },
  cardCoverList: { width: 100, height: 100, borderTopLeftRadius: 12, borderBottomLeftRadius: 12 },
  cardImagePlaceholder: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  cardTypeBadge: { position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(0, 0, 0, 0.6)', borderRadius: 12, padding: 4 },
  addFloatBtn: Platform.select({
    web: { position: 'absolute', top: 8, left: 8, width: 28, height: 28, borderRadius: 14, backgroundColor: '#ff6b35', alignItems: 'center', justifyContent: 'center', boxShadow: '0px 1px 2px rgba(0,0,0,0.1)' },
    default: { position: 'absolute', top: 8, left: 8, width: 28, height: 28, borderRadius: 14, backgroundColor: '#ff6b35', alignItems: 'center', justifyContent: 'center', shadowColor: '#000', shadowOpacity: 0.15, shadowOffset: { width: 0, height: 1 }, shadowRadius: 2, elevation: 3 }
  }),
  // 复制 CardPicker 的右侧纵向 Pill 样式
  listAddWrap: { position: 'absolute' as any, right: 0, top: 0, bottom: 0, width: 28, alignItems: 'center', justifyContent: 'center' },
  listAddPill: { width: 28, height: '100%', borderTopLeftRadius: 999, borderBottomLeftRadius: 999, backgroundColor: '#ff6b35', alignItems: 'center', justifyContent: 'center' },
  cardInfo: { padding: 12, flex: 1 },
  cardTitle: { fontSize: 16, fontWeight: '600', color: '#111827', marginBottom: 4 },
  privateBadge: { position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(79, 70, 229, 0.9)', borderRadius: 12, padding: 4 },
  cardAddress: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  cardAddressText: { fontSize: 14, color: '#6B7280', marginLeft: 4, flex: 1 },
  cardTagsContainer: { flexDirection: 'column' },
  cardTags: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' },
  cardTag: { backgroundColor: '#F3F4F6', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, marginRight: 4, marginBottom: 4 },
  cardTagText: { fontSize: 12, color: '#6B7280' },
  cardTagMore: { fontSize: 12, color: '#9CA3AF', marginLeft: 4 },

  loadingContainer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', paddingVertical: 20 },
  loadingText: { marginLeft: 8, fontSize: 14, color: '#6B7280' },

  noMoreContainer: { alignItems: 'center', paddingVertical: 20 },
  noMoreText: { fontSize: 14, color: '#9CA3AF' },

  emptyContainer: { alignItems: 'center', paddingVertical: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#111827', marginTop: 16, marginBottom: 8 },
  emptyText: { fontSize: 14, color: '#6B7280', textAlign: 'center' },

  paginationContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 12,
    flexWrap: 'nowrap',
    alignSelf: 'center',
    marginHorizontal: 12,
  },
  pageButton: { minWidth: 36, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 8, marginHorizontal: 4, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center' },
  pageButtonActive: { backgroundColor: '#ff6b35', borderColor: '#ff6b35' },
  pageButtonDisabled: { opacity: 0.5 },
  pageButtonText: { fontSize: 14, color: '#111827' },
  pageButtonTextActive: { color: '#fff', fontWeight: '700' },
  pageEllipsis: { fontSize: 18, color: '#9CA3AF', marginHorizontal: 6 },

  // Modal styles
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 16 },
  modalContent: { width: '100%', maxWidth: 640, backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  modalTitle: { fontSize: 16, fontWeight: '700', color: '#111827' },
  modalBody: { padding: 16 },
  modalCover: { width: '100%', height: 200, backgroundColor: '#F3F4F6', borderRadius: 8, marginBottom: 12, overflow: 'hidden' },
  modalRow: { flexDirection: 'row', alignItems: 'center', marginTop: 8 },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  modalBtn: { paddingHorizontal: 12, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#E5E7EB', backgroundColor: '#fff' },
  modalBtnPrimary: { backgroundColor: '#ff6b35', borderColor: '#ff6b35' },
  modalBtnText: { fontSize: 14, color: '#111827' },
  modalBtnTextPrimary: { color: '#fff', fontWeight: '700' },
});

export default CardListPage;